import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  Archive, Boxes, ClipboardCheck, Code2, Database, Gauge, KeyRound, LayoutDashboard, LogOut, Menu,
  PackagePlus, RotateCcw, ScanLine, Settings2, Store, UploadCloud, Users, Warehouse, X
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  brandingAssetUrl, defaultDeveloperSettings, DEVELOPER_SETTINGS_EVENT, loadDeveloperSettings,
  type DeveloperSettings
} from "../lib/developerSettings";

const groups = [
  {
    label: "Arbeitsbereich",
    items: [
      { to: "/", label: "Übersicht", icon: LayoutDashboard, end: true },
      { to: "/scanner-eingang", label: "Scanner-Eingang", icon: ScanLine },
      { to: "/wareneingaenge", label: "Wareneingänge", icon: PackagePlus },
      { to: "/lagerbestand", label: "Lagerbestand", icon: Boxes },
      { to: "/retouren", label: "Retouren", icon: RotateCcw },
      { to: "/abholungen", label: "Abholungen", icon: ClipboardCheck }
    ]
  },
  {
    label: "Organisation",
    items: [
      { to: "/easytec", label: "EasyTec-Uploads", icon: UploadCloud },
      { to: "/lieferanten", label: "Lieferanten", icon: Store },
      { to: "/stammdaten", label: "Stammdaten", icon: Settings2 },
      { to: "/konto", label: "Mein Konto", icon: KeyRound }
    ]
  }
];

export function AppShell() {
  const [open, setOpen] = useState(false);
  const [developerSettings, setDeveloperSettings] = useState<DeveloperSettings>(defaultDeveloperSettings);
  const { cloudConfigured, profile, signOut } = useAuth();
  const roleLabel = profile?.role === "admin" ? "Administrator" : profile?.role === "dispatcher" ? "Dispo" : "Lager";
  const warehouseLabel = profile?.warehouse?.name ?? "Beide Lager";
  const initials = (profile?.full_name ?? "K&S").split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
  const navigationGroups = groups.map((group) => {
    if (group.label !== "Organisation") return group;
    const allowedItems = group.items.filter((item) => !["/easytec", "/lieferanten"].includes(item.to) || profile?.role !== "warehouse");
    return profile?.role === "admin"
      ? { ...group, items: [
          ...allowedItems,
          { to: "/benutzer", label: "Benutzer", icon: Users },
          { to: "/system", label: "Systemstatus", icon: Database },
          { to: "/entwickler", label: "Entwickler", icon: Code2 }
        ] }
      : { ...group, items: allowedItems };
  });
  const reloadDeveloperSettings = useCallback(() => {
    void loadDeveloperSettings().then(setDeveloperSettings).catch(() => setDeveloperSettings(defaultDeveloperSettings));
  }, []);
  useEffect(() => {
    reloadDeveloperSettings();
    window.addEventListener(DEVELOPER_SETTINGS_EVENT, reloadDeveloperSettings);
    return () => window.removeEventListener(DEVELOPER_SETTINGS_EVENT, reloadDeveloperSettings);
  }, [reloadDeveloperSettings]);
  const logoUrl = brandingAssetUrl(developerSettings.branding.logoPath);
  const themeStyle = {
    "--blue": developerSettings.branding.primaryColor,
    "--blue-dark": developerSettings.branding.darkColor
  } as CSSProperties;

  return (
    <div className="app-shell" style={themeStyle}>
      <aside className={`sidebar ${open ? "sidebar--open" : ""}`}>
        <div className="brand">
          <span className={`brand__mark ${logoUrl ? "brand__mark--logo" : ""}`}>{logoUrl ? <img src={logoUrl} alt={`${developerSettings.branding.companyName} Logo`} /> : developerSettings.branding.companyName}</span>
          <span className="brand__copy"><strong>{developerSettings.branding.productName}</strong><small>{developerSettings.branding.adminSubtitle}</small></span>
          <button className="icon-button sidebar__close" type="button" aria-label="Menü schließen" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <nav className="nav">
          {navigationGroups.map((group) => (
            <section className="nav__group" key={group.label}>
              <span className="nav__label">{group.label}</span>
              {group.items.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => `nav__item ${isActive ? "nav__item--active" : ""}`} onClick={() => setOpen(false)}>
                  <item.icon size={19} strokeWidth={1.7} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </section>
          ))}
        </nav>
        <footer className="sidebar__profile">
          <span className="avatar">{initials}</span>
          <span><strong>{profile?.full_name ?? "K&S Benutzer"}</strong><small>{cloudConfigured ? roleLabel : "Lokale Vorschau"}</small></span>
          {cloudConfigured
            ? <button className="icon-button" type="button" aria-label="Abmelden" title="Abmelden" onClick={signOut}><LogOut size={18} /></button>
            : <Database size={18} />}
        </footer>
      </aside>
      {open && <button className="sidebar-scrim" type="button" aria-label="Menü schließen" onClick={() => setOpen(false)} />}
      <section className="workspace">
        <header className="topbar">
          <button className="icon-button topbar__menu" type="button" aria-label="Menü öffnen" onClick={() => setOpen(true)}><Menu size={21} /></button>
          <span className="topbar__crumb"><Gauge size={18} /> {developerSettings.branding.companyName} {developerSettings.branding.productName}</span>
          <span className="warehouse-switch"><Warehouse size={16} /> {warehouseLabel}</span>
        </header>
        {!cloudConfigured && <div className="dev-banner"><Archive size={16} /><span><strong>Lokale Vorschau:</strong> Noch keine Testcloud verbunden. Angezeigte Inhalte sind neutrale Testdaten.</span></div>}
        <main className="workspace__content"><Outlet context={{ developerSettings }} /></main>
      </section>
    </div>
  );
}
